import { Input } from '@/components/ui/input';
import {
  useAuthoredRows,
  useCreateAuthoredRow,
  useDeleteAuthoredRow,
  useUpdateAuthoredRow,
  type AuthoredCollection,
} from 'components/IAP/hooks/icsAuthoredFormHooks';
import { useRecordFormVersionSave } from 'components/IAP/hooks/icsFormVersionHooks';
import { useICSContext } from 'components/IAP/planningView/ICSContext';

import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import SaveableField from '../SaveableField';
import {
  ICS220_AIRCRAFT_COLUMNS,
  ICS220_ASTRONOMICAL_FIELDS,
  ICS220_BOX_LABELS,
  ICS220_FREQUENCY_ROWS,
  ICS220_PERSONNEL_ROWS,
  ICS220_READY_AIRCRAFT_FIELDS,
  ICS220_SORTIE_COLUMNS,
  ICS220_TFR_FIELDS,
} from '../ics220BoxLabels';
import {
  BoxRowTable,
  NewCgFormCard,
  NewCgHeaderBoxes,
  NewCgPreparedByBox,
  type BoxColumn,
} from './newCgFormBoxes';

/** A numbered box whose content is a period-scoped table with an Add row. */
function PeriodTableBox({
  boxLabel,
  testId,
  collection,
  identityColumn,
  columns,
  periodNumber,
  onSaved,
  addLabel,
}: {
  boxLabel: string;
  testId: string;
  collection: AuthoredCollection;
  identityColumn: string;
  columns: readonly BoxColumn[];
  periodNumber: number;
  onSaved: () => void;
  addLabel: string;
}) {
  const { data: rows = [] } = useAuthoredRows(collection, periodNumber);
  const createRow = useCreateAuthoredRow(collection);
  const updateRow = useUpdateAuthoredRow(collection);
  const deleteRow = useDeleteAuthoredRow(collection);

  return (
    <Ics204CardSection boxLabel={boxLabel} testId={testId} className="tw:mt-0">
      <BoxRowTable
        columns={columns}
        rows={rows}
        identityColumn={identityColumn}
        testId={`${testId}-table`}
        addLabel={addLabel}
        onSave={(entityId, patch) =>
          updateRow.mutate({ entityId, ...patch }, { onSuccess: onSaved })
        }
        onDelete={(entityId) =>
          deleteRow.mutate(entityId, { onSuccess: onSaved })
        }
        onAdd={async () => {
          const created = await createRow.mutateAsync({
            operational_period_number: periodNumber,
          });
          onSaved();
          return String(created?.[identityColumn] ?? '');
        }}
      />
    </Ics204CardSection>
  );
}

/**
 * ICS 220-CG, boxed as the sheet prints it.
 *
 * Boxes 8 and 9 are name/phone and primary/secondary grids against a fixed
 * list of seats and nets, so they are stored whole in the summary's `personnel`
 * and `frequencies` JSON rather than as rows.
 */
const Ics220Card = () => {
  const { effectivePeriodNumber } = useICSContext();
  const { data: summaries = [] } = useAuthoredRows(
    'airOpsSummaries',
    effectivePeriodNumber
  );
  const createSummary = useCreateAuthoredRow('airOpsSummaries');
  const updateSummary = useUpdateAuthoredRow('airOpsSummaries');
  const recordVersionSave = useRecordFormVersionSave(
    'ICS_220',
    effectivePeriodNumber
  );
  const onSuccess = () => recordVersionSave.mutate();
  const header = summaries[0] ?? null;

  const personnel =
    (header?.personnel as Record<string, Record<string, string>>) ?? {};
  const frequencies =
    (header?.frequencies as Record<string, Record<string, string>>) ?? {};

  const saveHeader = (patch: Record<string, unknown>) => {
    if (!header) {
      createSummary.mutate(
        { operational_period_number: effectivePeriodNumber, ...patch },
        { onSuccess }
      );
      return;
    }
    updateSummary.mutate(
      { entityId: String(header.summary_id), ...patch },
      { onSuccess }
    );
  };

  /** A box of single blanks driven by a label list, laid out side by side. */
  const blankGrid = (
    fields: readonly { readonly key: string; readonly label: string }[]
  ) => (
    <div className="tw:grid tw:gap-3 tw:sm:grid-cols-2">
      {fields.map((field) => (
        <SaveableField
          key={field.key}
          id={`ics220-${field.key}`}
          label={field.label}
          value={String(header?.[field.key] ?? '')}
          onSave={(value) => saveHeader({ [field.key]: value })}
        />
      ))}
    </div>
  );

  /** Boxes 8 and 9: a fixed row list, each row with the same named blanks. */
  const namedRowGrid = (
    rows: readonly { readonly key: string; readonly label: string }[],
    values: Record<string, Record<string, string>>,
    blanks: readonly string[],
    column: 'personnel' | 'frequencies'
  ) => (
    <table className="tw:w-full tw:text-sm">
      <thead>
        <tr>
          <th className="tw:px-2 tw:py-1 tw:text-left tw:font-normal tw:text-muted-foreground" />
          {blanks.map((blank) => (
            <th
              key={blank}
              className="tw:px-2 tw:py-1 tw:text-left tw:font-normal tw:text-muted-foreground"
            >
              {blank}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            <td className="tw:px-2 tw:py-1">{row.label}</td>
            {blanks.map((blank) => (
              <td key={blank} className="tw:px-1 tw:py-1">
                <Input
                  aria-label={`${row.label} ${blank}`}
                  defaultValue={values[row.key]?.[blank] ?? ''}
                  onBlur={(event) => {
                    const value = event.target.value;
                    if (value === (values[row.key]?.[blank] ?? '')) return;
                    saveHeader({
                      [column]: {
                        ...values,
                        [row.key]: { ...(values[row.key] ?? {}), [blank]: value },
                      },
                    });
                  }}
                />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <NewCgFormCard
      title="Air Operations Summary ICS-220"
      formname="ICS_220_CG"
      baseId="ICS_220"
      exportLabel="Preview ICS-220"
    >
      <NewCgHeaderBoxes labels={ICS220_BOX_LABELS} testIdPrefix="ics220" />

      <Ics204CardSection
        boxLabel={ICS220_BOX_LABELS.astronomicalData}
        testId="ics220-section-astronomical-data"
        className="tw:mt-0"
      >
        {blankGrid(ICS220_ASTRONOMICAL_FIELDS)}
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS220_BOX_LABELS.remarks}
        testId="ics220-section-remarks"
        className="tw:mt-0"
      >
        <SaveableField
          id="ics220-remarks"
          label="Remarks"
          hideLabel
          multiline
          placeholder="Safety notes, hazards, air ops special instructions..."
          value={String(header?.remarks ?? '')}
          onSave={(value) => saveHeader({ remarks: value })}
        />
      </Ics204CardSection>

      <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-2">
        <Ics204CardSection
          boxLabel={ICS220_BOX_LABELS.readyAircraft}
          testId="ics220-section-ready-aircraft"
          className="tw:mt-0 tw:h-full tw:min-w-0"
        >
          {blankGrid(ICS220_READY_AIRCRAFT_FIELDS)}
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS220_BOX_LABELS.tfrInfo}
          testId="ics220-section-tfr-info"
          className="tw:mt-0 tw:h-full tw:min-w-0"
        >
          {blankGrid(ICS220_TFR_FIELDS)}
        </Ics204CardSection>
      </div>

      <Ics204CardSection
        boxLabel={ICS220_BOX_LABELS.personnel}
        testId="ics220-section-personnel"
        className="tw:mt-0"
      >
        {namedRowGrid(
          ICS220_PERSONNEL_ROWS,
          personnel,
          ['Name', 'Phone Number'],
          'personnel'
        )}
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS220_BOX_LABELS.frequencies}
        testId="ics220-section-frequencies"
        className="tw:mt-0"
      >
        {namedRowGrid(
          ICS220_FREQUENCY_ROWS,
          frequencies,
          ['Primary', 'Secondary'],
          'frequencies'
        )}
      </Ics204CardSection>

      <PeriodTableBox
        boxLabel={ICS220_BOX_LABELS.assignedAircraft}
        testId="ics220-section-assigned-aircraft"
        collection="airOpsAircraft"
        identityColumn="aircraft_id"
        columns={ICS220_AIRCRAFT_COLUMNS}
        periodNumber={effectivePeriodNumber}
        onSaved={onSuccess}
        addLabel="Add Aircraft"
      />

      <PeriodTableBox
        boxLabel={ICS220_BOX_LABELS.assignments}
        testId="ics220-section-assignments"
        collection="airOpsSorties"
        identityColumn="sortie_id"
        columns={ICS220_SORTIE_COLUMNS}
        periodNumber={effectivePeriodNumber}
        onSaved={onSuccess}
        addLabel="Add Assignment"
      />

      <NewCgPreparedByBox
        baseId="ICS_220"
        boxLabel={ICS220_BOX_LABELS.preparedBy}
        testIdPrefix="ics220"
      />
    </NewCgFormCard>
  );
};

export default Ics220Card;
